/**
 * UploadProgressBar — floating bottom-center toast that shows the live
 * MB/MB + percentage of any large request (photo upload) in flight.
 *
 * Subscribes to /lib/uploadProgress so the same component covers
 * AddCat, AIIdentify, EmergencyReport, etc. — anywhere apiClient is used.
 *
 * Positioned ABOVE the bottom dock (~96px) so it never gets covered.
 *
 * On completion: fires a tiny haptic pulse (Android / iOS supported
 * browsers ignore the call gracefully) and swaps the icon to a green
 * check so the user gets a clear "done" signal.
 */
import { useEffect, useRef, useState } from "react";
import { UploadCloud, CheckCircle2 } from "lucide-react";
import confetti from "canvas-confetti";
import { subscribe } from "../lib/uploadProgress";

const MB = 1024 * 1024;

function fmt(bytes) {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function UploadProgressBar() {
  const [state, setState] = useState({ active: false, loaded: 0, total: 0 });
  // Track previous "done" state so we only fire haptic once per upload.
  const lastDoneRef = useRef(false);

  useEffect(() => subscribe(setState), []);

  // Fire micro-haptic + brand confetti exactly once when crossing 100%.
  useEffect(() => {
    const done = state.total > 0 && state.loaded >= state.total;
    if (done && !lastDoneRef.current) {
      lastDoneRef.current = true;
      try {
        if (typeof navigator !== "undefined" && navigator.vibrate) {
          navigator.vibrate(20);
        }
      } catch {
        /* no-op — vibration API not supported / blocked */
      }
      // Tiny brand-tinted confetti burst, anchored near the toast (bottom
      // center, just above the dock). Respects reduced-motion.
      try {
        const prefersReduced =
          typeof window !== "undefined" &&
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!prefersReduced) {
          confetti({
            particleCount: 36,
            spread: 64,
            startVelocity: 32,
            gravity: 0.9,
            ticks: 140,
            scalar: 0.78,
            origin: { x: 0.5, y: 0.85 },
            colors: ["#e11d48", "#ea580c", "#f59e0b", "#10b981", "#ffffff"],
            disableForReducedMotion: true,
          });
        }
      } catch {
        /* canvas-confetti can throw on very old browsers — non-critical */
      }
    }
    if (!state.active && state.loaded === 0) {
      lastDoneRef.current = false;
    }
  }, [state.loaded, state.total, state.active]);

  if (!state.active && state.loaded === 0) return null;
  const pct = state.total > 0 ? Math.min(100, Math.round((state.loaded / state.total) * 100)) : 0;
  const done = pct >= 100;

  return (
    <div
      className={`upload-progress-toast ${done ? "is-done" : ""}`}
      data-testid="upload-progress-toast"
      data-state={done ? "done" : "uploading"}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {done ? (
          <CheckCircle2 size={14} className="text-emerald-600 upload-check-pop" />
        ) : (
          <UploadCloud size={14} className="text-orange-600" />
        )}
        <span
          className={`text-[12px] font-semibold ${done ? "text-emerald-700" : "text-stone-800"}`}
          data-testid="upload-progress-label"
        >
          {done ? "Yüklendi" : "Fotoğraf yükleniyor"}
        </span>
        <span className="ml-auto text-[11px] tabular-nums text-stone-500">
          {fmt(state.loaded)} / {fmt(state.total)} · {pct}%
        </span>
      </div>
      <div className="upload-progress-track" aria-hidden>
        <div className="upload-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
